<script>
	import moment from 'moment';
	import metadata from 'core/metadata';
	import metadataUtils from 'metadata-utils';
	import productService from 'services/productService';
	import { fireEvent, events } from 'utilities/analyticsUtilities';
	const SCOPE_SEQUENCE_PROMO_CLICK = events.SCOPE_SEQUENCE_PROMO_CLICK;

	const KINDERGARTEN_ID = 16;
	const scopeAndSequence = metadata.scopeAndSequence;
	let ssDataRaw = { products: [], meta: {} };

	if (scopeAndSequence && scopeAndSequence.length) {
		scopeAndSequence.every(s => {
			if (s.meta && s.meta.key === 'topical') {
				ssDataRaw = s;
				return false;
			}
			return true;
		});
	}

	export default {
		components: {
			'article-image': require('components/article-image.vue').default
		},
		props: {
			changeSearchView: {
				default: function () {
					return;
				}
			}
		},
		data() {
			return {
				productImages: {},
				ssData: ssDataRaw,
				currentMonth: null,
				weekOfMonth: null
			};
		},
		methods: {
			getGradeName(id) {
				let key = '_' + id;
				let node = metadata.allActiveNodesById[key];
				let gradeName = '';
				if (node && node.name) {
					if (key === '_' + KINDERGARTEN_ID) {
						gradeName = 'Kindergarten';
					} else {
						gradeName = metadataUtils.getOrdinal(node.name) + ' Grade';
					}
				}
				return gradeName;
			},
			/**
			 * Count the number of "weeks" in a month have passed
			 * 	up to the date provided.
			 *
			 * "weeks" are currently counted by the number of Mondays
			 * 	and the first week isn't over until we've seen a full Mon-Sun
			 */
			weekOfMonthForMoment(start) {
				start = start.clone();
				let week = 0;
				let d = start.clone().date(1);
				let date = d.date();
				let startDate = start.date();

				// if first day of the month is Monday, count as week 1
				if (d.day() === 1) {
					week = 1;
				}

				for (let i = date; i < startDate; i++) {
					d.add(1, 'day');
					//Count "full" week Mon-Sun
					if (d.day() === 1) {
						week += 1;
					}
				}

				if (week < 1) {
					week = 1;
				}

				return week;
			},
			logPromoEvent(grade) {
				fireEvent(SCOPE_SEQUENCE_PROMO_CLICK, {
					month: this.ssDataMonthKeys[this.currentMonth],
					weekOfMonth: this.weekOfMonth,
					grade
				});
			},
			/**
			 * Start with today and step forward until we find a matching week with data.
			 *
			 * Only the first 3 weeks of the month are valid,
			 * 	once we reach a 4th, jump to the first day of the next month
			 *
			 * "week" of the month is calculated by weekOfMonthForMoment
			 */
			getBestMonthWeek() {
				let date = moment();
				let themesByMonthKeyAndWeek = this.themesByMonthKeyAndWeek;
				let ssDataMonthKeys = this.ssDataMonthKeys;
				let currentMonth = null;
				let weekOfMonth = null;

				// 52 attempts should look more than a year forward
				for (let i = 0; i < 52; i++) {
					let monthName = date.format('MMMM');
					let monthKey = ssDataMonthKeys[date.format('MMMM')];
					let calculatedWeekOfMonth = this.weekOfMonthForMoment(date);
					if (
						monthKey &&
						calculatedWeekOfMonth &&
						calculatedWeekOfMonth <= 3 &&
						themesByMonthKeyAndWeek[monthKey] &&
						themesByMonthKeyAndWeek[monthKey][calculatedWeekOfMonth]
					) {
						// Found a week in the first 3 weeks of a month with data
						currentMonth = monthName;
						weekOfMonth = calculatedWeekOfMonth;
						break;
					} else if (calculatedWeekOfMonth > 3) {
						// After Third week, move to 1st day of next month
						date.add(1, 'month').date(1);
					} else {
						// Not yet past third week, but no data
						date.add(1, 'week');
					}
				}

				this.currentMonth = currentMonth;
				this.weekOfMonth = weekOfMonth;
			},
			onScopeAndSequenceClick(opts) {
				this.changeSearchView('scope-and-sequence', opts);
				if (opts.selectedGrades) {
					this.logPromoEvent(opts.selectedGrades);
				} else {
					this.logPromoEvent();
				}
			},
			async setProductImages() {
				let productImages = {};
				const promises = this.productsByGrade.map(({ pid }) => {
					return productService.get(pid).then(({ thumbnail, imgAlt }) => {
						productImages[pid] = {
							thumbnail,
							imgAlt
						};
					});
				});

				await Promise.all(promises);
				this.productImages = productImages;
			}
		},
		computed: {
			/**
			 * Return object where o[ssDataMonthKey][weekNumber] is a string containing the
			 * theme or null if there are no products for Kindergarten (the source of truth)
			 */
			themesByMonthKeyAndWeek() {
				const lookup = {};
				this.ssData.products.forEach(p => {
					if (p.grade === KINDERGARTEN_ID) {
						const month = '' + p.month;
						const week = '' + p.week;
						if (!lookup[month]) {
							lookup[month] = {
								1: null,
								2: null,
								3: null,
								4: null,
								5: null
							};
						}

						lookup[month][week] = p.theme;
					}
				});
				return lookup;
			},
			/**
			 * Return object where o[capitalized-month-name] === key-in-ssData
			 */
			ssDataMonthKeys() {
				const keys = {};
				Object.keys(this.ssData.meta.titles).forEach(k => {
					keys[this.ssData.meta.titles[k]] = k;
				});
				return keys;
			},
			currentTopic() {
				let weekOfMonth = this.weekOfMonth;
				let currentMonth = this.currentMonth;
				let ssDataMonthKeys = this.ssDataMonthKeys;
				let themesByMonthKeyAndWeek = this.themesByMonthKeyAndWeek;
				let monthKey = ssDataMonthKeys[currentMonth];

				return themesByMonthKeyAndWeek[monthKey]['' + weekOfMonth];
			},
			weekOfMonthDisplay() {
				const week = this.weekOfMonth;
				switch (week) {
					case 1:
						return 'first';
					case 2:
						return 'second';
					case 3:
						return 'third';
					case 4:
						return 'fourth';
					default:
						return 'fifth';
				}
			},
			productsByGrade() {
				let ssData = this.ssData;
				let weekOfMonth = this.weekOfMonth;
				let currentMonth = this.currentMonth;
				let ssDataMonthKeys = this.ssDataMonthKeys;
				let monthKey = ssDataMonthKeys[currentMonth];

				return ssData.products
					.filter(p => {
						return p.week === weekOfMonth && p.month === parseInt(monthKey);
					})
					.sort((a, b) => a.grade - b.grade);
			}
		},
		created() {
			this.getBestMonthWeek();
			this.setProductImages();
		}
	};
</script>
<style lang="scss">
	.promotion-scope-and-sequence {
		.scope-and-sequence-button {
			width: fit-content;
			margin: auto;
			margin-bottom: 15px;
		}
	}
</style>
<template>
	<div
		class="promotion-panel promotion-scope-and-sequence"
		v-if="productsByGrade.length > 0">
		<h2 class="has-sub-heading">Idea for the {{ weekOfMonthDisplay }} week of {{ currentMonth }}: {{ currentTopic }}</h2>
		<div class="sub-heading">Assign a month of Article-A-Day for K-8 with the Scope &amp; Sequences tool!</div>
		<div class="scope-and-sequence-button">
			<button
				class="button"
				role="button"
				@click="onScopeAndSequenceClick({ selectedSet: 'topical', expanded: ssDataMonthKeys[currentMonth] })">
				Assign Scope &amp; Sequences
			</button>
		</div>
		<div class="featured-topic-row">
			<a
				v-for="p in productsByGrade"
				class="featured-topic scope-and-sequence-featured-topic"
				:key="p.pid"
				href="javascript:void(0)"
				:aria-label="getGradeName(p.grade)"
				@click="onScopeAndSequenceClick({ selectedSet: 'topical', expanded: ssDataMonthKeys[currentMonth], selectedGrades: p.grade })">
				<template v-if="productImages[p.pid]">
					<article-image
						:image-url="productImages[p.pid].thumbnail"
						:alt-text="productImages[p.pid].imgAlt"></article-image>
					<div class="featured-topic-name">{{ getGradeName(p.grade) }}</div>
				</template>
			</a>
		</div>
	</div>
</template>
